import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import SubmissionFormDialog from "@/components/SubmissionFormDialog";
import {
    BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";
import {
    Plus, FileText, DollarSign, CheckCircle2, Pencil, Trash2, Search, SlidersHorizontal, TrendingUp,
} from "lucide-react";
import { format } from "date-fns";
import { fmtDate } from "@/lib/formatDate";
import { toast } from "sonner";

const STATUS_BADGES = {
    Pending: { bg: "#FEF9C3", text: "#A16207", dot: "#EAB308" },
    Quoted: { bg: "#DBEAFE", text: "#1D4ED8", dot: "#3B82F6" },
    Bound: { bg: "#D1FAE5", text: "#047857", dot: "#10B981" },
    Declined: { bg: "#FEE2E2", text: "#B91C1C", dot: "#EF4444" },
};

const STATUSES = ["Pending", "Quoted", "Bound", "Declined"];

function fmtMoney(n) {
    if (!n) return "$0";
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
    return `$${Math.round(n).toLocaleString()}`;
}

function fmtMonth(my) {
    if (!my) return "—";
    const [y, m] = my.split("-");
    return new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1).toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

function MetricCard({ label, value, accent, icon: Icon, sub, testid }) {
    const tints = {
        blue: { bg: "bg-blue-50", icon: "text-blue-600" },
        emerald: { bg: "bg-emerald-50", icon: "text-emerald-600" },
        indigo: { bg: "bg-indigo-50", icon: "text-indigo-600" },
        amber: { bg: "bg-amber-50", icon: "text-amber-600" },
    };
    const t = tints[accent] || tints.blue;
    return (
        <div className="ph-card p-5 hover:shadow-ph-soft transition-shadow" data-testid={testid}>
            <div className={`w-9 h-9 rounded-lg ${t.bg} flex items-center justify-center mb-4`}>
                <Icon className={`w-4 h-4 ${t.icon}`} strokeWidth={2} />
            </div>
            <div className="ph-label">{label}</div>
            <div className="mt-1 text-2xl font-semibold text-ph-text tabular-nums">{value}</div>
            {sub && <div className="mt-1 text-xs text-ph-mutedSoft">{sub}</div>}
        </div>
    );
}

export default function Submissions() {
    const [subs, setSubs] = useState([]);
    const [monthly, setMonthly] = useState({ series: [], totals: { estimated_premium: 0, bound_premium: 0, submission_count: 0, bound_count: 0 } });
    const [brokers, setBrokers] = useState([]);

    const [search, setSearch] = useState("");
    const [monthF, setMonthF] = useState("all");
    const [brokerF, setBrokerF] = useState("all");
    const [statusF, setStatusF] = useState("all");
    const [lobF, setLobF] = useState("all");

    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [deletingNow, setDeletingNow] = useState(false);

    const load = async () => {
        try {
            const [s, m, b] = await Promise.all([
                api.get("/submissions"),
                api.get("/submissions/monthly?months=12"),
                api.get("/brokers"),
            ]);
            setSubs(s.data);
            setMonthly(m.data);
            setBrokers(b.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
        }
    };
    useEffect(() => { load(); }, []);

    const monthOptions = useMemo(() => {
        const set = new Set(subs.map((s) => s.month_year).filter(Boolean));
        return Array.from(set).sort().reverse();
    }, [subs]);

    const brokerOptions = useMemo(() => {
        const set = new Set(subs.map((s) => s.broker_name).filter(Boolean));
        return Array.from(set).sort();
    }, [subs]);

    const lobOptions = useMemo(() => {
        const set = new Set(subs.map((s) => s.lob).filter(Boolean));
        return Array.from(set).sort();
    }, [subs]);

    const filtered = useMemo(() => {
        return subs.filter((s) => {
            if (search && !`${s.broker_name} ${s.named_insured} ${s.notes}`.toLowerCase().includes(search.toLowerCase())) return false;
            if (monthF !== "all" && s.month_year !== monthF) return false;
            if (brokerF !== "all" && s.broker_name !== brokerF) return false;
            if (statusF !== "all" && s.status !== statusF) return false;
            if (lobF !== "all" && s.lob !== lobF) return false;
            return true;
        });
    }, [subs, search, monthF, brokerF, statusF, lobF]);

    const onDelete = async () => {
        if (!deleting) return;
        setDeletingNow(true);
        try {
            await api.delete(`/submissions/${deleting.id}`);
            toast.success("Submission deleted");
            setDeleting(null);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
        } finally {
            setDeletingNow(false);
        }
    };

    const fmt = (d) => fmtDate(d);

    const chartData = monthly.series.map((m) => ({
        month: fmtMonth(m.month_year),
        Estimated: m.estimated_premium,
        Bound: m.bound_premium,
        count: m.count,
    }));

    const bind_rate = monthly.totals.submission_count
        ? Math.round((monthly.totals.bound_count / monthly.totals.submission_count) * 100)
        : 0;

    return (
        <div className="p-6 lg:p-8 space-y-6 animate-fade-in" data-testid="submissions-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <h1 className="ph-h1">Submissions</h1>
                    <p className="text-sm text-ph-muted mt-1">
                        Month-to-month tracking of broker submissions and the premium bound by the BD team.
                    </p>
                </div>
                <Button
                    onClick={() => { setEditing(null); setFormOpen(true); }}
                    className="bg-ph-primary text-white font-medium h-9 px-3 hover:bg-ph-primaryHover"
                    data-testid="add-submission-btn"
                >
                    <Plus className="w-4 h-4 mr-1.5" /> Log submission
                </Button>
            </div>

            {/* Top metrics */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <MetricCard label="Submissions (12 mo)" value={monthly.totals.submission_count} icon={FileText} accent="blue" testid="metric-sub-count" />
                <MetricCard label="Estimated premium" value={fmtMoney(monthly.totals.estimated_premium)} icon={DollarSign} accent="indigo" testid="metric-sub-est" sub="Across all submissions" />
                <MetricCard label="Bound premium" value={fmtMoney(monthly.totals.bound_premium)} icon={CheckCircle2} accent="emerald" testid="metric-sub-bound" sub={`${monthly.totals.bound_count} bound`} />
                <MetricCard label="Bind rate" value={`${bind_rate}%`} icon={TrendingUp} accent="amber" testid="metric-sub-bind-rate" sub="Bound ÷ Submitted" />
            </section>

            {/* Monthly chart */}
            <section className="ph-card p-5">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="ph-h2">Monthly premium trend</h2>
                    <span className="text-[11px] text-ph-mutedSoft">Last 12 months</span>
                </div>
                <div className="h-72">
                    <ResponsiveContainer>
                        <BarChart data={chartData} margin={{ top: 5, right: 8, bottom: 5, left: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                            <XAxis dataKey="month" stroke="#9CA3AF" tickLine={false} axisLine={false} fontSize={11} />
                            <YAxis stroke="#9CA3AF" tickLine={false} axisLine={false} fontSize={11}
                                tickFormatter={(v) => v >= 1_000_000 ? `$${(v / 1_000_000).toFixed(1)}M` : v >= 1_000 ? `$${(v / 1_000).toFixed(0)}K` : `$${v}`}
                            />
                            <Tooltip
                                contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }}
                                cursor={{ fill: "#F1F5F9" }}
                                formatter={(v) => fmtMoney(v)}
                            />
                            <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                            <Bar dataKey="Estimated" fill="#93C5FD" radius={[6, 6, 0, 0]} />
                            <Bar dataKey="Bound" fill="#10B981" radius={[6, 6, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </section>

            {/* Filters */}
            <div className="ph-card p-4">
                <div className="flex items-center gap-2 mb-3">
                    <SlidersHorizontal className="w-3.5 h-3.5 text-ph-muted" />
                    <span className="text-xs font-medium text-ph-text">Filters</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    <div className="relative md:col-span-1">
                        <Search className="absolute left-3 top-3 w-4 h-4 text-ph-mutedSoft" />
                        <Input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10" data-testid="submissions-search-input" />
                    </div>
                    <Select value={monthF} onValueChange={setMonthF}>
                        <SelectTrigger className="h-10" data-testid="submissions-month-filter"><SelectValue placeholder="Month" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All months</SelectItem>
                            {monthOptions.map((m) => <SelectItem key={m} value={m}>{fmtMonth(m)}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={brokerF} onValueChange={setBrokerF}>
                        <SelectTrigger className="h-10" data-testid="submissions-broker-filter"><SelectValue placeholder="Broker" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All brokers</SelectItem>
                            {brokerOptions.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={statusF} onValueChange={setStatusF}>
                        <SelectTrigger className="h-10" data-testid="submissions-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={lobF} onValueChange={setLobF}>
                        <SelectTrigger className="h-10" data-testid="submissions-lob-filter"><SelectValue placeholder="LOB" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All LOBs</SelectItem>
                            {lobOptions.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Table */}
            <div className="ph-card overflow-hidden">
                <div className="max-h-[calc(100vh-360px)] overflow-auto scrollbar-thin">
                    <table className="w-full text-sm" data-testid="submissions-table">
                        <thead className="bg-ph-canvas text-[11px] font-medium text-ph-muted sticky top-0 z-10 border-b border-ph-border">
                            <tr>
                                <th className="text-left px-4 py-2.5">Effective</th>
                                <th className="text-left px-4 py-2.5">Broker</th>
                                <th className="text-left px-4 py-2.5">Named Insured</th>
                                <th className="text-left px-4 py-2.5">LOB</th>
                                <th className="text-right px-4 py-2.5">Estimated</th>
                                <th className="text-right px-4 py-2.5">Bound</th>
                                <th className="text-left px-4 py-2.5">Status</th>
                                <th className="text-left px-4 py-2.5">Notes</th>
                                <th className="text-right px-4 py-2.5">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 && (
                                <tr><td colSpan={9} className="text-center py-12 text-ph-muted">
                                    No submissions match these filters. Log your first submission to start tracking premium.
                                </td></tr>
                            )}
                            {filtered.map((s) => {
                                const sb = STATUS_BADGES[s.status] || STATUS_BADGES.Pending;
                                return (
                                    <tr key={s.id} className="border-t border-ph-borderSoft hover:bg-ph-canvas" data-testid={`submission-row-${s.id}`}>
                                        <td className="px-4 py-3 text-ph-muted whitespace-nowrap">{fmt(s.effective_date)}</td>
                                        <td className="px-4 py-3 font-medium text-ph-text">{s.broker_name}</td>
                                        <td className="px-4 py-3 text-ph-text">{s.named_insured}</td>
                                        <td className="px-4 py-3">
                                            <span className="inline-flex px-2 py-0.5 rounded-full bg-ph-canvas border border-ph-border text-[11px] font-medium text-ph-muted">{s.lob || "—"}</span>
                                        </td>
                                        <td className="px-4 py-3 text-right tabular-nums text-ph-text">{fmtMoney(s.estimated_premium)}</td>
                                        <td className="px-4 py-3 text-right tabular-nums font-semibold" style={{ color: s.bound_premium > 0 ? "#047857" : "#9CA3AF" }}>
                                            {fmtMoney(s.bound_premium)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ backgroundColor: sb.bg, color: sb.text }}>
                                                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: sb.dot }} />
                                                {s.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-ph-muted max-w-xs truncate" title={s.notes}>{s.notes || "—"}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    onClick={() => { setEditing(s); setFormOpen(true); }}
                                                    className="text-ph-muted hover:text-ph-primary transition-colors p-1.5 rounded hover:bg-ph-primarySoft"
                                                    data-testid={`edit-submission-${s.id}`}
                                                    title="Edit"
                                                >
                                                    <Pencil className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => setDeleting(s)}
                                                    className="text-ph-muted hover:text-rose-600 transition-colors p-1.5 rounded hover:bg-rose-50"
                                                    data-testid={`delete-submission-${s.id}`}
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="px-4 py-2.5 text-xs text-ph-muted border-t border-ph-border bg-ph-canvas">
                    {filtered.length} of {subs.length} submissions
                </div>
            </div>

            <SubmissionFormDialog
                open={formOpen}
                onOpenChange={(o) => { setFormOpen(o); if (!o) setEditing(null); }}
                submission={editing}
                brokers={brokers}
                onSuccess={load}
            />

            <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete submission?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {deleting?.broker_name} → {deleting?.named_insured} ({fmtMoney(deleting?.estimated_premium)} est.) will be permanently removed.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={onDelete} disabled={deletingNow} className="bg-rose-600 hover:bg-rose-700 text-white">
                            {deletingNow ? "Deleting…" : "Delete"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
